import React, { useEffect, useState, useRef } from "react";
import { api, BACKEND_URL, formatDate, formatDateTime, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ClipboardList, Truck, MessageSquareWarning, PackageCheck, Send, Camera, Trash2, X, UserSquare, AlertCircle, History, BookOpen } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";
import { useNavigate } from "react-router-dom";
import StockHistoryTable from "@/components/StockHistoryTable";
import MitraGuide, { shouldAutoShowGuide, markGuideSeen } from "@/components/MitraGuide";
import { DismissibleMemo } from "@/components/DismissibleMemo";

export default function MitraPortal() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [opnameOpen, setOpnameOpen] = useState(false);
  const [opnameItems, setOpnameItems] = useState({});
  const [opnameNotes, setOpnameNotes] = useState("");
  const [opnameDate, setOpnameDate] = useState(new Date().toISOString().slice(0, 10));
  const [photo, setPhoto] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [cForm, setCForm] = useState({ category: "pengiriman", subject: "", message: "", priority: "normal" });
  const [busy, setBusy] = useState(false);
  const [stockHistory, setStockHistory] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [shipmentPhoto, setShipmentPhoto] = useState(null);

  const loadAll = () => Promise.all([
    api.get("/mitra/stock-opname").then((r) => setData(r.data)),
    api.get("/mitra/complaints").then((r) => setComplaints(r.data)),
    api.get("/mitra/me").then((r) => setMe(r.data)),
    api.get("/mitra/stock-history").then((r) => setStockHistory(r.data)).catch(() => {}),
  ]);
  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    if (shouldAutoShowGuide()) { setGuideOpen(true); markGuideSeen(); }
  }, []);

  // Compute "last shipment per product"
  const lastShipmentDate = (pid) => {
    if (!data?.shipments) return null;
    for (const d of data.shipments) {
      if ((d.items || []).some((it) => it.product_id === pid)) return d.date;
    }
    return null;
  };

  const shipmentPhotoUrl = (shipment) => {
    if (shipment.photo) return shipment.photo;
    const token = localStorage.getItem("hotto_token") || "";
    return `${BACKEND_URL}/api/distribution-photos/${shipment.photo_id}?auth=${encodeURIComponent(token)}`;
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("Foto maksimal 3 MB");
    const reader = new FileReader();
    reader.onloadend = () => setPhoto(reader.result);
    reader.readAsDataURL(f);
  };

  const submitOpname = async () => {
    if (!canOpname) {
      toast.error(opnameBlockReason || "Opname belum bisa disubmit saat ini");
      return;
    }
    const items = (data?.aggregate || []).map((p) => ({
      product_id: p.product_id,
      product_name: p.product_name,
      physical_qty: Number(opnameItems[p.product_id] ?? 0),
      system_qty: p.saldo_sistem,
      variance: Number(opnameItems[p.product_id] ?? 0) - p.saldo_sistem,
    }));
    if (items.length === 0) return toast.error("Tidak ada produk untuk diopname");
    setBusy(true);
    try {
      const { data: resp } = await api.post("/mitra/stock-opname", {
        items, notes: opnameNotes, opname_date: opnameDate, photo,
      });
      if (resp.adjustment_created) {
        toast.warning(`Opname terkirim dengan ${resp.variance_count} selisih — sedang menunggu review supplier. Cek "Riwayat Opname" untuk status.`, { duration: 6000 });
      } else {
        toast.success("Opname terkirim — semua sesuai! Tidak perlu adjustment.");
      }
      setOpnameOpen(false); setOpnameItems({}); setOpnameNotes(""); setPhoto(null);
      loadAll();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const submitComplaint = async () => {
    if (!cForm.subject.trim() || !cForm.message.trim()) return toast.error("Isi subjek dan pesan");
    try {
      await api.post("/mitra/complaints", cForm);
      toast.success("Keluhan terkirim, supplier akan segera merespon");
      setComplaintOpen(false);
      setCForm({ category: "pengiriman", subject: "", message: "", priority: "normal" });
      loadAll();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const delComplaint = async (c) => {
    if (c.auto_generated) return toast.error("Keluhan otomatis (sistem) tidak dapat dihapus manual");
    if (!window.confirm("Hapus keluhan ini?")) return;
    try { await api.delete(`/mitra/complaints/${c.id}`); toast.success("Dihapus"); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const shipTF = useTableFilter(data?.shipments || [], {
    searchKeys: ["date", "notes"], defaultSort: { key: "date", dir: "desc" },
  });
  const complaintTF = useTableFilter(complaints, {
    searchKeys: ["subject", "category", "message"], defaultSort: { key: "created_at", dir: "desc" },
  });

  if (!data) return <div className="p-4 text-slate-500">Memuat portal mitra...</div>;

  const openComplaintCount = complaints.filter((c) => c.status !== "responded").length;

  // === Two-Phase Reconciliation gating ===
  // Fase 1 = menunggu supplier tutup input penjualan | Fase 2 = opname aktif | Fase 3 = tutup buku (lewat tgl 10)
  const phase = me?.current_phase || 1;
  const opnameSubmitted = !!me?.opname_submitted;
  const canOpname = phase === 2 && !opnameSubmitted;
  const opnameBlockReason = opnameSubmitted
    ? "Opname bulan ini sudah dikirim."
    : phase === 1
      ? "Menunggu Supplier menginput laporan penjualan retail bulan lalu untuk toko Anda."
      : phase === 3
        ? "Sudah lewat batas opname (tgl 10). Periode ini tutup buku."
        : "";

  return (
    <>
      <PageHeader
        title="Portal Mitra Toko"
        subtitle={`Selamat datang, ${user?.name}`}
        testId="page-mitra"
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={() => setGuideOpen(true)} data-testid="btn-open-guide">
            <BookOpen className="w-4 h-4 mr-2"/> Panduan
          </Button>
          <Button variant="outline" onClick={() => setComplaintOpen(true)} data-testid="btn-complaint">
            <MessageSquareWarning className="w-4 h-4 mr-2"/> Kirim Keluhan
          </Button>
          <Button
            onClick={() => canOpname ? setOpnameOpen(true) : toast.warning(opnameBlockReason)}
            data-testid="btn-opname"
            disabled={!canOpname}
            className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PackageCheck className="w-4 h-4 mr-2"/> Input Stok Opname
          </Button>
        </div>}
      />

      {/* Profile completeness reminder */}
      {me && !me.profile_complete && (
        <Card className="p-3 border-2 border-amber-300 bg-amber-50 mb-4 flex items-start gap-3" data-testid="profile-reminder">
          <UserSquare className="w-5 h-5 text-amber-700 shrink-0 mt-0.5"/>
          <div className="flex-1 text-sm text-amber-800">
            <b>Lengkapi Identitas Toko!</b> Kolom yang belum diisi: <b>{me.profile_missing.join(", ")}</b>.
            Data ini penting untuk kelancaran koordinasi dengan supplier.
          </div>
          <Button size="sm" onClick={() => nav("/mitra/identitas")} className="bg-amber-600 hover:bg-amber-700 text-white shrink-0" data-testid="btn-goto-identitas">
            Lengkapi Sekarang
          </Button>
        </Card>
      )}

      {/* Workflow info opname — Two-Phase Reconciliation */}
      <DismissibleMemo
        id="mitra-opname-workflow"
        className={`p-4 pr-12 border-l-4 mb-4 ${
          phase === 1 ? "border-l-amber-500 bg-amber-50/60"
          : phase === 2 ? "border-l-emerald-500 bg-emerald-50/40"
          : "border-l-slate-500 bg-slate-50"
        }`}
        testId="opname-workflow-info"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className={`w-5 h-5 shrink-0 mt-0.5 ${phase === 1 ? "text-amber-700" : phase === 2 ? "text-emerald-700" : "text-slate-600"}`}/>
          <div className="text-sm text-slate-700 flex-1">
            <div className={`font-bold mb-1 ${phase === 1 ? "text-amber-800" : phase === 2 ? "text-emerald-800" : "text-slate-800"}`}>
              Alur Two-Phase Reconciliation
            </div>
            <div className="text-xs space-y-1">
              <ol className="list-decimal ml-5 mt-0.5 space-y-0.5">
                <li><b>Fase 1:</b> Supplier menginput laporan penjualan retail <b>bulan lalu</b> untuk toko Anda (boleh bertanggal akhir bulan lalu maupun bulan ini).</li>
                <li><b>Fase 2 (s/d tgl 10):</b> Setelah Supplier input min. 1 laporan penjualan bulan lalu → Anda langsung bisa hitung stok fisik + upload foto, lalu submit opname. Opname bulan berjalan merekonsiliasi penjualan bulan lalu.</li>
                <li><b>Fase 3 (setelah tgl 10):</b> Tutup buku. Opname tidak bisa disubmit — koordinasi manual dengan Supplier.</li>
                <li>Jika ada <b>selisih</b>, otomatis dikirim ke Supplier untuk review (approve / tolak / custom).</li>
              </ol>
              {me && (
                <div className={`mt-2 pt-2 border-t ${phase === 1 ? "border-amber-200" : phase === 2 ? "border-emerald-200" : "border-slate-200"}`}>
                  {opnameSubmitted ? (
                    <span className="text-emerald-700"><b>✓ Opname bulan ini sudah dikirim</b> pada {me.opname_this_month?.opname_date}. Menunggu review Supplier bila ada selisih.</span>
                  ) : phase === 1 ? (
                    <span className="text-amber-800"><b>⏳ FASE 1 — Hari ini tgl {me.today_day}.</b> Supplier belum menginput laporan penjualan retail bulan lalu untuk toko Anda. Anda belum bisa submit opname.</span>
                  ) : phase === 2 ? (
                    <span className="text-emerald-800"><b>✅ FASE 2 — Opname AKTIF (hari ini tgl {me.today_day}).</b> Supplier sudah menginput laporan penjualan untuk toko Anda. Silakan hitung stok fisik dan submit sekarang. Deadline tgl 10.</span>
                  ) : (
                    <span className="text-red-700"><b>🔒 FASE 3 — TUTUP BUKU (hari ini tgl {me.today_day}).</b> Sudah lewat batas opname. Hubungi Supplier untuk penanganan manual.</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </DismissibleMemo>

      {/* Keluhan aktif banner */}
      {openComplaintCount > 0 && (
        <Card className="p-3 border-l-4 border-l-red-500 bg-red-50 mb-4 flex items-center gap-3">
          <MessageSquareWarning className="w-5 h-5 text-red-600 shrink-0"/>
          <div className="text-sm text-red-800">
            <b>{openComplaintCount} keluhan/notifikasi aktif</b> — cek tab "Keluhan Saya" di bawah.
          </div>
        </Card>
      )}

      <Tabs defaultValue="stock">
        <TabsList className="mb-4">
          <TabsTrigger value="stock" data-testid="tab-stock"><ClipboardList className="w-4 h-4 mr-2"/>Rekap Stok</TabsTrigger>
          <TabsTrigger value="shipments" data-testid="tab-shipments"><Truck className="w-4 h-4 mr-2"/>Riwayat Kiriman ({data.shipments.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-stock-history"><History className="w-4 h-4 mr-2"/>Riwayat Stok</TabsTrigger>
          <TabsTrigger value="complaints" data-testid="tab-complaints-mitra">
            <MessageSquareWarning className="w-4 h-4 mr-2"/>Keluhan Saya
            {openComplaintCount > 0 && <span className="ml-2 px-1.5 rounded bg-red-500 text-white text-[10px]">{openComplaintCount}</span>}
          </TabsTrigger>
        </TabsList>

        {/* ==== STOCK ==== */}
        <TabsContent value="stock">
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="w-10">#</TableHead>
                <TableHead>Produk</TableHead>
                <TableHead className="text-right">Total Diterima</TableHead>
                <TableHead className="text-right">Sudah Dilaporkan</TableHead>
                <TableHead className="text-right">Saldo Sistem</TableHead>
                <TableHead>Terakhir Dikirim</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(data.aggregate || []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Belum ada kiriman produk.</TableCell></TableRow>}
                {(data.aggregate || []).map((p, idx) => {
                  const last = lastShipmentDate(p.product_id);
                  return (
                    <TableRow key={p.product_id} className="table-row-hover">
                      <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                      <TableCell className="font-medium">{p.product_name}</TableCell>
                      <TableCell className="text-right font-mono-num">{p.total_kirim}</TableCell>
                      <TableCell className="text-right font-mono-num text-emerald-700">{p.total_lapor}</TableCell>
                      <TableCell className="text-right font-mono-num font-bold text-[var(--hotto-primary)]">{p.saldo_sistem}</TableCell>
                      <TableCell className="text-sm text-slate-500">{last ? formatDate(last) : "-"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ==== SHIPMENTS ==== */}
        <TabsContent value="shipments">
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <div className="p-3 border-b"><TableToolbar tf={shipTF} placeholder="Cari tanggal / catatan..."/></div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <SortHeader tf={shipTF} col="date">Tanggal Kirim</SortHeader>
                <TableHead>Detail Item</TableHead>
                <TableHead className="w-28 text-right">Bukti Foto</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {shipTF.rows.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-8 text-slate-500">Belum ada kiriman.</TableCell></TableRow>}
                {shipTF.rows.map((d) => (
                  <TableRow key={d.id} className="table-row-hover">
                    <TableCell className="font-mono-num text-sm">{formatDate(d.date)}</TableCell>
                    <TableCell>
                      <div className="text-sm">{d.items.map((it) => `${it.qty} × ${it.product_name}`).join(", ")}</div>
                      {d.notes && <div className="text-xs text-slate-500 italic mt-1">{d.notes}</div>}
                    </TableCell>
                    <TableCell className="text-right">
                      {d.photo_id || d.photo ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setShipmentPhoto(d)}
                          data-testid={`btn-view-shipment-photo-${d.id}`}
                        >
                          <Camera className="w-3 h-3 mr-1" /> Lihat
                        </Button>
                      ) : (
                        <span className="text-xs text-slate-400">Belum ada</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ==== STOCK HISTORY (Kartu Stok Gabungan) ==== */}
        <TabsContent value="history">
          {stockHistory ? (
            <StockHistoryTable data={stockHistory} storeName={me?.store?.name || user?.name} testPrefix="mitra-stock-history" />
          ) : (
            <div className="p-6 text-center text-slate-500">Memuat riwayat stok...</div>
          )}
        </TabsContent>

        {/* ==== COMPLAINTS ==== */}
        <TabsContent value="complaints">
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <div className="p-3 border-b"><TableToolbar tf={complaintTF} placeholder="Cari keluhan..."/></div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <SortHeader tf={complaintTF} col="created_at">Tanggal</SortHeader>
                <SortHeader tf={complaintTF} col="category">Kategori</SortHeader>
                <SortHeader tf={complaintTF} col="subject">Subjek</SortHeader>
                <SortHeader tf={complaintTF} col="priority">Prio</SortHeader>
                <SortHeader tf={complaintTF} col="status">Status</SortHeader>
                <TableHead className="w-12"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {complaintTF.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Belum ada keluhan.</TableCell></TableRow>}
                {complaintTF.rows.map((c) => (
                  <TableRow key={c.id} className="table-row-hover">
                    <TableCell className="text-xs font-mono-num">{formatDateTime(c.created_at)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="uppercase text-[10px]">{c.category}</Badge>
                      {c.auto_generated && <Badge variant="outline" className="text-[10px] ml-1 bg-slate-100">sistem</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-sm">{c.subject}</div>
                      <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-line">{c.message}</div>
                      {c.response && <div className="text-xs text-emerald-700 mt-1 bg-emerald-50 border border-emerald-200 rounded p-2"><b>Respon Supplier:</b> {c.response}</div>}
                    </TableCell>
                    <TableCell><Badge className={c.priority === "high" ? "bg-red-100 text-red-700 hover:bg-red-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}>{c.priority}</Badge></TableCell>
                    <TableCell><Badge className={c.status === "responded" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>{c.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      {!c.auto_generated && (
                        <Button size="icon" variant="ghost" onClick={() => delComplaint(c)} data-testid={`del-c-${c.id}`}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ==== STOCK OPNAME DIALOG ==== */}
      <Dialog open={opnameOpen} onOpenChange={setOpnameOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Input Stok Opname</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-700 bg-blue-50 border border-blue-200 rounded p-3">
              <b>Alur bila ada selisih:</b>
              <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                <li>Anda submit hasil hitung fisik (isi qty fisik + foto bukti).</li>
                <li>Sistem otomatis buat <b>permintaan adjustment</b> untuk direview supplier.</li>
                <li>Supplier memutuskan: <b>Setujui</b> (stok sistem diubah sesuai fisik), <b>Tolak</b> (stok sistem tetap), atau <b>Custom</b> (per-item).</li>
                <li>Anda menerima notifikasi hasilnya di tab "Keluhan Saya" & bisa cek di <i>Riwayat Opname</i>.</li>
              </ol>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tanggal Opname</Label>
                <Input type="date" value={opnameDate} onChange={(e) => setOpnameDate(e.target.value)} data-testid="input-opname-date"/>
              </div>
              <div>
                <Label>Upload Foto Fisik</Label>
                <div className="flex gap-2">
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden"/>
                  <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden"/>
                  <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="btn-pick-gallery">Galeri</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => cameraRef.current?.click()} data-testid="btn-camera">
                    <Camera className="w-3 h-3 mr-1"/> Kamera
                  </Button>
                </div>
                {photo && (
                  <div className="relative mt-2 inline-block">
                    <img src={photo} alt="preview" className="h-16 rounded border"/>
                    <button onClick={() => setPhoto(null)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"><X className="w-3 h-3"/></button>
                  </div>
                )}
              </div>
            </div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Produk</TableHead>
                <TableHead className="text-right">Sistem</TableHead>
                <TableHead className="text-right w-32">Hitung Fisik</TableHead>
                <TableHead className="text-right">Selisih</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(data.aggregate || []).map((p) => {
                  const phys = Number(opnameItems[p.product_id] ?? "");
                  const variance = phys - p.saldo_sistem;
                  const filled = opnameItems[p.product_id] !== undefined && opnameItems[p.product_id] !== "";
                  return (
                    <TableRow key={p.product_id}>
                      <TableCell className="font-medium">{p.product_name}</TableCell>
                      <TableCell className="text-right font-mono-num">{p.saldo_sistem}</TableCell>
                      <TableCell className="text-right">
                        <Input type="number" className="h-8 text-right font-mono-num"
                          value={opnameItems[p.product_id] ?? ""}
                          onChange={(e) => setOpnameItems({ ...opnameItems, [p.product_id]: e.target.value })}
                          data-testid={`opname-${p.product_id}`}/>
                      </TableCell>
                      <TableCell className={`text-right font-mono-num ${!filled ? "text-slate-400" : variance === 0 ? "text-slate-400" : variance > 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {filled ? (variance >= 0 ? "+" : "") + variance : "-"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div><Label>Catatan (opsional)</Label><Textarea rows={2} value={opnameNotes} onChange={(e) => setOpnameNotes(e.target.value)}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpnameOpen(false)}>Batal</Button>
            <Button onClick={submitOpname} disabled={busy} data-testid="btn-submit-opname" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
              <Send className="w-4 h-4 mr-2"/> {busy ? "Mengirim..." : "Kirim ke Supplier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!shipmentPhoto} onOpenChange={(isOpen) => !isOpen && setShipmentPhoto(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Bukti Foto Pengiriman</DialogTitle></DialogHeader>
          {shipmentPhoto && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600">
                Kiriman {formatDate(shipmentPhoto.date)}
              </div>
              <img
                src={shipmentPhotoUrl(shipmentPhoto)}
                alt="Bukti pengiriman ke toko"
                className="max-h-[65vh] w-full rounded border object-contain"
                data-testid="shipment-photo-preview"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==== COMPLAINT DIALOG ==== */}
      <Dialog open={complaintOpen} onOpenChange={setComplaintOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Kirim Keluhan / Pertanyaan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kategori</Label>
                <Select value={cForm.category} onValueChange={(v) => setCForm({ ...cForm, category: v })}>
                  <SelectTrigger data-testid="select-c-category"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pengiriman">Pengiriman</SelectItem>
                    <SelectItem value="retur">Retur Barang</SelectItem>
                    <SelectItem value="produk">Kualitas Produk</SelectItem>
                    <SelectItem value="lainnya">Lainnya</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Prioritas</Label>
                <Select value={cForm.priority} onValueChange={(v) => setCForm({ ...cForm, priority: v })}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Tinggi (Urgent)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Subjek</Label><Input value={cForm.subject} onChange={(e) => setCForm({ ...cForm, subject: e.target.value })} data-testid="input-c-subject"/></div>
            <div><Label>Detail Pesan</Label><Textarea rows={4} value={cForm.message} onChange={(e) => setCForm({ ...cForm, message: e.target.value })} data-testid="input-c-message"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComplaintOpen(false)}>Batal</Button>
            <Button onClick={submitComplaint} data-testid="btn-submit-complaint" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
              <Send className="w-4 h-4 mr-2"/> Kirim
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==== PANDUAN / TUTORIAL MITRA ==== */}
      <MitraGuide open={guideOpen} onOpenChange={setGuideOpen} />
    </>
  );
}
